import { useEffect, useRef } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { Sparkle } from "@/components/icons";

const COLORS = ["#ffd23f", "#ff4d8d", "#38b6ff", "#2fb344", "#ff7a2f", "#b79bff"];
const LOOP_SECONDS = 60; // time to scroll one copy of the content (same speed as before)

// Horizontal-only ticker: translate3d(x, 0, 0) driven by rAF, x always wrapped inside one copy
// width, so the loop is seamless. Drag (mouse/touch/pen) and horizontal trackpad scroll move it;
// touch-action: pan-y keeps the page's vertical scroll native.
const useTicker = (wrapRef, trackRef) => {
  useEffect(() => {
    const wrap = wrapRef.current;
    const track = trackRef.current;
    if (!wrap || !track) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let x = 0;
    let half = 0;
    let hover = false;
    let drag = null;
    let last = performance.now();
    let raf = 0;
    const norm = (v) => (half ? (((v % half) + half) % half) - half : v);
    const apply = () => { track.style.transform = `translate3d(${x}px, 0, 0)`; };
    const measure = () => { half = track.firstElementChild?.getBoundingClientRect().width || 0; x = norm(x); apply(); };
    const tick = (now) => {
      const dt = Math.min(now - last, 100) / 1000;
      last = now;
      if (!reduce && !hover && !drag && half) { x = norm(x - (half / LOOP_SECONDS) * dt); apply(); }
      raf = visible ? requestAnimationFrame(tick) : 0;
    };
    // Off screen or hidden tab: the loop stops completely (no CPU), and resumes where it was.
    let visible = true;
    const io = new IntersectionObserver(([e]) => {
      const was = visible;
      visible = e.isIntersecting && document.visibilityState === "visible";
      if (visible && !was && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
    });
    io.observe(wrap);
    const vis = () => { visible = document.visibilityState === "visible"; if (visible && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); } };
    document.addEventListener("visibilitychange", vis);
    const down = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (e.pointerType === "mouse") e.preventDefault();
      drag = { id: e.pointerId, startX: e.clientX, from: x };
      try { wrap.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }
    };
    const move = (e) => { if (drag && e.pointerId === drag.id) { x = norm(drag.from + e.clientX - drag.startX); apply(); } };
    const up = (e) => { if (drag && e.pointerId === drag.id) { drag = null; last = performance.now(); } };
    const enter = (e) => { if (e.pointerType === "mouse") hover = true; };
    const leave = (e) => { if (e.pointerType === "mouse") hover = false; };
    const wheel = (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) { x = norm(x - e.deltaX); apply(); } };
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    measure();
    raf = requestAnimationFrame(tick);
    wrap.addEventListener("pointerdown", down);
    wrap.addEventListener("pointermove", move);
    ["pointerup", "pointercancel", "lostpointercapture"].forEach((n) => wrap.addEventListener(n, up));
    wrap.addEventListener("pointerenter", enter);
    wrap.addEventListener("pointerleave", leave);
    wrap.addEventListener("wheel", wheel, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      document.removeEventListener("visibilitychange", vis);
      ro.disconnect();
      wrap.removeEventListener("pointerdown", down);
      wrap.removeEventListener("pointermove", move);
      ["pointerup", "pointercancel", "lostpointercapture"].forEach((n) => wrap.removeEventListener(n, up));
      wrap.removeEventListener("pointerenter", enter);
      wrap.removeEventListener("pointerleave", leave);
      wrap.removeEventListener("wheel", wheel);
    };
  }, [wrapRef, trackRef]);
};

export const Marquee = () => {
  const { t, lang } = useI18n();
  const { content } = useSite();
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  useTicker(wrapRef, trackRef);
  const custom = content?.texts?.[lang]?.marquee;
  const items = custom ? custom.split("|").map((s) => s.trim()).filter(Boolean) : t.marquee;
  const row = [...items, ...items, ...items];
  return (
    <div className="overflow-hidden relative z-10">
    <div ref={wrapRef} className="relative bg-ink py-5 sm:py-6 marquee-wrap overflow-hidden border-y-[3px] border-ink select-none cursor-grab active:cursor-grabbing" style={{ touchAction: "pan-y" }} data-testid="marquee">
      <div ref={trackRef} className="marquee-track flex w-max will-change-transform">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0 items-center" aria-hidden={k === 1}>
            {row.map((txt, i) => (
              <span key={i} className="flex items-center">
                <span className="font-display font-bold text-2xl sm:text-4xl px-6 whitespace-nowrap" style={{ color: COLORS[i % COLORS.length] }}>
                  {txt}
                </span>
                <Sparkle className="w-6 h-6 sm:w-8 sm:h-8" color="#fff6e0" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
    </div>
  );
};
